import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Confetti from 'react-confetti';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaPlay, FaArrowRight } from 'react-icons/fa';
import { Zap, BookOpen, Trophy, Sparkles } from 'lucide-react';

const easing = [0.22, 1, 0.36, 1];

const CongratulationsPreview = ({ auth }) => {
  const navigate = useNavigate();
  const [showConfetti, setShowConfetti] = useState(true);
  const [windowDimension, setWindowDimension] = useState({ width: 300, height: 300 });

  const user = auth.user;
  const firstName = user?.name?.split(' ')[0] || 'Student';
  const pathTitle = user?.path?.title || 'Personal Brand Trailblazer';
  const programTitle = user?.group?.program?.title || 'the program';
  const pathDescription = user?.path?.description || 'As a Personal Brand Trailblazer, you\'re stepping into CareerReady Connect to shape the story only you can tell by discovering your strengths, building a confident and authentic voice, and creating a digital presence that stands out. This path is all about growing your personal brand, connecting it to real opportunities, and showing the world exactly who you are and what you\'re ready to bring.';
  const coursesCount = user?.path?.courses?.length || 0;

  useEffect(() => {
    setWindowDimension({ width: window.innerWidth, height: window.innerHeight });

    const timer = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  if (!auth.isAuthenticated) {
    navigate('/');
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {showConfetti && (
        <Confetti
          width={windowDimension.width}
          height={windowDimension.height}
          gravity={0.25}
          numberOfPieces={250}
          recycle={false}
          colors={['hsl(var(--primary))', '#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4']}
        />
      )}

      <div className="max-w-6xl mx-auto">
        {/* Top bar */}
        <div className="border-b border-border bg-background px-5 sm:px-10 py-3.5 flex items-center justify-between">
          <div className="leading-tight min-w-0">
            <p className="font-display font-bold text-foreground tracking-tight truncate text-[15px] sm:text-base">
              {programTitle}
            </p>
            {user?.group?.title && (
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground/55">
                {user.group.title}
              </p>
            )}
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            <span className="flex items-center gap-1.5 text-sm font-bold text-foreground tabular-nums">
              <Zap className="w-4 h-4 text-xp" />
              0 XP
            </span>
          </div>
        </div>

        {/* Hero section */}
        <div className="bg-background px-5 sm:px-10 pt-10 sm:pt-16 pb-7 sm:pb-9">
          <motion.div
            className="flex items-center gap-2.5 mb-4"
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.05, ease: easing }}
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary" aria-hidden="true" />
            <span className="text-[10px] font-bold text-primary uppercase tracking-[0.22em]">
              Assessment Complete
            </span>
          </motion.div>

          <motion.h1
            className="font-display font-bold text-foreground tracking-tight leading-[1.05]"
            style={{ fontSize: 'clamp(28px, 4.5vw, 48px)', letterSpacing: '-0.025em' }}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.12, ease: easing }}
          >
            Welcome aboard, {firstName}.
          </motion.h1>

          <motion.p
            className="mt-2 text-[14px] sm:text-[15px] font-semibold uppercase tracking-[0.16em] text-foreground/55"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.22, ease: easing }}
          >
            Your journey starts now
          </motion.p>
        </div>

        <div className="h-px bg-border mx-5 sm:mx-10" />

        {/* Path assignment card */}
        <div className="px-5 sm:px-10 py-8 sm:py-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3, ease: easing }}
          >
            <p className="text-[10px] font-bold text-primary uppercase tracking-[0.22em] mb-5">
              Your Assigned Path
            </p>

            <div className="bg-card border border-border rounded-2xl p-6 sm:p-8">
              <div className="flex items-start gap-4 sm:gap-5">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="font-display font-bold text-foreground text-xl sm:text-2xl tracking-tight mb-1">
                    {pathTitle}
                  </h2>
                  {pathDescription && (
                    <p className="text-foreground/60 text-sm sm:text-[15px] leading-relaxed mt-2">
                      {pathDescription}
                    </p>
                  )}
                  {coursesCount > 0 && (
                    <div className="flex items-center gap-3 mt-4">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold">
                        <BookOpen className="w-3.5 h-3.5" />
                        {coursesCount} {coursesCount === 1 ? 'Course' : 'Courses'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="h-px bg-border mx-5 sm:mx-10" />

        {/* Welcome video */}
        <motion.div
          className="px-5 sm:px-10 py-8 sm:py-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4, ease: easing }}
        >
          <p className="text-[10px] font-bold text-primary uppercase tracking-[0.22em] mb-5">
            Welcome Video
          </p>

          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/embed/Frb72eDy5fs"
                title="Welcome Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </motion.div>

        <div className="h-px bg-border mx-5 sm:mx-10" />

        {/* Stats row */}
        <motion.div
          className="px-5 sm:px-10 py-8 sm:py-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5, ease: easing }}
        >
          <p className="text-[10px] font-bold text-primary uppercase tracking-[0.22em] mb-5">
            What's Next
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-card border border-border rounded-xl p-5">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center mb-3">
                <FaCheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <p className="font-bold text-foreground text-sm mb-1">Assessment Done</p>
              <p className="text-foreground/50 text-xs leading-relaxed">
                You've completed your initial assessment successfully.
              </p>
            </div>

            <div className="bg-card border border-border rounded-xl p-5">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <p className="font-bold text-foreground text-sm mb-1">Path Assigned</p>
              <p className="text-foreground/50 text-xs leading-relaxed">
                Your personalized learning path is ready for you.
              </p>
            </div>

            <div className="bg-card border border-border rounded-xl p-5">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center mb-3">
                <Trophy className="w-5 h-5 text-amber-500" />
              </div>
              <p className="font-bold text-foreground text-sm mb-1">Start Earning XP</p>
              <p className="text-foreground/50 text-xs leading-relaxed">
                Complete lessons and courses to earn XP and badges.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Continue button */}
        <motion.div
          className="px-5 sm:px-10 pb-10 sm:pb-14"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6, ease: easing }}
        >
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full sm:w-auto flex items-center justify-center gap-2.5 bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-3.5 px-8 rounded-xl transition-colors duration-200 text-sm tracking-wide"
          >
            Continue to Dashboard
            <FaArrowRight className="w-3.5 h-3.5" />
          </button>
        </motion.div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth
});

export default connect(mapStateToProps)(CongratulationsPreview);
